import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import { supabase } from '../../supabase';
import {
  FiTag,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiCopy,
  FiRefreshCw,
  FiPercent,
  FiHash,
} from 'react-icons/fi';
import '../styles/eventsManagement.css';
import '../styles/AdminShared.css';

const DISCOUNT_TYPES = [
  { value: 'percent', label: 'Prosent (%)' },
  { value: 'fixed',   label: 'Fast beløp (kr)' },
];

const initialForm = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  minOrderValue: '',
  maxUses: '',
  expiryDate: '',
  noExpiry: false,
  isActive: true,
};

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    if (i === 4) code += '-';
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const [y, m, day] = expiryDate.split('-');
  return new Date(y, m - 1, day, 23, 59, 59) < new Date();
};

// Maps a discount_codes row (snake_case) to the camelCase shape this page
// already renders (matches the old Firestore doc fields).
function toCodeShape(row) {
  return {
    id: row.id,
    code: row.code,
    description: row.description,
    discountType: row.type,
    discountValue: row.value,
    minOrderValue: row.min_total,
    maxUses: row.max_uses,
    usedCount: row.uses_count,
    expiryDate: row.expires_at ? new Date(row.expires_at).toISOString().slice(0, 10) : null,
    noExpiry: row.no_expiry,
    isActive: row.active,
    createdAt: row.created_at,
  };
}

export default function RabattkodeManagement() {
  const [form, setForm] = useState(initialForm);
  const [codes, setCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copied, setCopied] = useState(null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const fetchCodes = async () => {
      const { data, error } = await supabase.from('discount_codes').select('*').order('created_at', { ascending: false });
      if (error) { console.error('Failed to fetch discount codes:', error); return; }
      setCodes((data || []).map(toCodeShape));
    };
    fetchCodes();

    const channel = supabase
      .channel('discount_codes_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'discount_codes' }, fetchCodes)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const onChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.code.trim()) return setFormError('Kode er påkrevd.');
    if (!form.discountValue || isNaN(Number(form.discountValue)) || Number(form.discountValue) <= 0)
      return setFormError('Skriv inn en gyldig rabattverdi.');
    if (form.discountType === 'percent' && Number(form.discountValue) > 100)
      return setFormError('Prosent kan ikke overstige 100.');
    if (!form.noExpiry && !form.expiryDate)
      return setFormError('Velg utløpsdato eller merk "Ingen utløpsdato".');

    // Check for duplicate code
    const duplicate = codes.find(c => c.code.toUpperCase() === form.code.trim().toUpperCase());
    if (duplicate) return setFormError('En kode med dette navnet finnes allerede.');

    setSaving(true);
    try {
      const { error } = await supabase.from('discount_codes').insert({
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        type: form.discountType,
        value: Number(form.discountValue),
        min_total: form.minOrderValue ? Number(form.minOrderValue) : null,
        max_uses: form.maxUses ? Number(form.maxUses) : null,
        uses_count: 0,
        expires_at: form.noExpiry ? null : form.expiryDate,
        no_expiry: form.noExpiry,
        active: form.isActive,
      });
      if (error) throw error;
      setForm(initialForm);
    } catch (err) {
      setFormError('Kunne ikke lagre kode: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    await supabase.from('discount_codes').update({ active: !current }).eq('id', id);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await supabase.from('discount_codes').delete().eq('id', deleteTarget);
    setDeleteTarget(null);
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(code);
    setTimeout(() => setCopied(null), 1800);
  };

  const activeCount = codes.filter(c => c.isActive && !isExpired(c.expiryDate)).length;
  const totalUses = codes.reduce((s, c) => s + (c.usedCount || 0), 0);

  const confirmModal = deleteTarget
    ? createPortal(
        <div style={overlay} onClick={() => setDeleteTarget(null)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', color: '#010f3c' }}>Slett rabattkode?</h3>
            <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 14 }}>
              Dette kan ikke angres. Koden vil slutte å virke umiddelbart.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="events-secondary-btn" onClick={() => setDeleteTarget(null)}>Avbryt</button>
              <button className="events-danger-btn" onClick={handleDelete}>Slett</button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <AdminDashboardNav />
      <div className="events-container">

        {/* Header */}
        <div className="coupon-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#010f3c' }}><FiTag size={28} /></span>
            <div>
              <h1 style={{ margin: 0 }}>Rabattkoder</h1>
              <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Opprett og administrer rabattkoder for nettbutikken</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className="events-stat-pill"><FiCheckCircle size={14} /> {activeCount} aktive</span>
            <span className="events-stat-pill"><FiHash size={14} /> {codes.length} totalt</span>
            <span className="events-stat-pill"><FiPercent size={14} /> {totalUses} brukt</span>
          </div>
        </div>

        {/* Create form */}
        <div className="events-create-card" style={{ marginBottom: 24 }}>
          <div className="events-card-header">
            <FiPlus size={18} />
            <span>Ny rabattkode</span>
          </div>
          <form onSubmit={handleSubmit}>
            {/* Code + generate */}
            <div className="events-form-grid events-form-grid-2">
              <label className="events-label">
                Kode
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="events-input"
                    value={form.code}
                    onChange={e => onChange('code', e.target.value.toUpperCase())}
                    placeholder="F.eks. SOMMER25"
                    maxLength={20}
                    style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}
                  />
                  <button
                    type="button"
                    title="Generer tilfeldig kode"
                    onClick={() => onChange('code', generateCode())}
                    style={{
                      padding: '0 14px', borderRadius: 8, border: '2px solid #e2e8f0',
                      background: '#f8fafc', cursor: 'pointer', color: '#010f3c',
                      display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FiRefreshCw size={14} /> Generer
                  </button>
                </div>
              </label>

              <label className="events-label">
                Beskrivelse (valgfri)
                <input
                  className="events-input"
                  value={form.description}
                  onChange={e => onChange('description', e.target.value)}
                  placeholder="F.eks. 10% på neste bestilling"
                />
              </label>
            </div>

            {/* Discount type + value */}
            <div className="events-form-grid events-form-grid-2">
              <label className="events-label">
                Rabatttype
                <select
                  className="events-input"
                  value={form.discountType}
                  onChange={e => onChange('discountType', e.target.value)}
                >
                  {DISCOUNT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>

              <label className="events-label">
                Rabattverdi {form.discountType === 'percent' ? '(%)' : '(kr)'}
                <input
                  className="events-input"
                  type="number"
                  min="0"
                  max={form.discountType === 'percent' ? '100' : undefined}
                  value={form.discountValue}
                  onChange={e => onChange('discountValue', e.target.value)}
                  placeholder={form.discountType === 'percent' ? 'F.eks. 10' : 'F.eks. 50'}
                />
              </label>
            </div>

            {/* Min order + max uses */}
            <div className="events-form-grid events-form-grid-2">
              <label className="events-label">
                Min. bestillingsverdi (kr, valgfri)
                <input
                  className="events-input"
                  type="number"
                  min="0"
                  value={form.minOrderValue}
                  onChange={e => onChange('minOrderValue', e.target.value)}
                  placeholder="F.eks. 200"
                />
              </label>

              <label className="events-label">
                Maks antall bruk (valgfri)
                <input
                  className="events-input"
                  type="number"
                  min="1"
                  value={form.maxUses}
                  onChange={e => onChange('maxUses', e.target.value)}
                  placeholder="La stå tom = ubegrenset"
                />
              </label>
            </div>

            {/* Expiry */}
            <div className="events-form-grid events-form-grid-1">
              <label className="events-label">
                Utløpsdato
                <input
                  className="events-input"
                  type="date"
                  value={form.expiryDate}
                  onChange={e => onChange('expiryDate', e.target.value)}
                  disabled={form.noExpiry}
                  style={{ opacity: form.noExpiry ? 0.4 : 1 }}
                />
              </label>
            </div>

            {/* Checkboxes */}
            <div style={{ display: 'flex', gap: 24, padding: '0 2rem 1.5rem', flexWrap: 'wrap' }}>
              <label className="events-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => onChange('isActive', e.target.checked)}
                  className="events-checkbox"
                />
                Aktiv med én gang
              </label>
              <label className="events-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.noExpiry}
                  onChange={e => onChange('noExpiry', e.target.checked)}
                  className="events-checkbox"
                />
                Ingen utløpsdato
              </label>
            </div>

            {formError && (
              <p style={{ color: '#dc2626', fontSize: 13, padding: '0 2rem 1rem', margin: 0 }}>{formError}</p>
            )}

            <div style={{ padding: '0 2rem 1.5rem' }}>
              <button type="submit" className="events-primary-btn" disabled={saving}>
                {saving ? 'Lagrer…' : 'Opprett rabattkode'}
              </button>
            </div>
          </form>
        </div>

        {/* Code list */}
        <div className="events-list-card">
          <div className="events-card-header">
            <FiTag size={18} />
            <span>Alle rabattkoder ({codes.length})</span>
          </div>

          {codes.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
              <FiTag size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p style={{ margin: 0 }}>Ingen rabattkoder enda. Opprett den første ovenfor.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Kode', 'Beskrivelse', 'Rabatt', 'Min. ordre', 'Maks bruk', 'Brukt', 'Utløper', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#010f3c', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c, i) => {
                    const expired = isExpired(c.expiryDate);
                    const maxReached = c.maxUses !== null && c.usedCount >= c.maxUses;
                    const statusColor = c.isActive && !expired && !maxReached ? '#15803d' : '#dc2626';
                    const statusBg   = c.isActive && !expired && !maxReached ? '#f0fdf4' : '#fef2f2';
                    const statusBorder = c.isActive && !expired && !maxReached ? '#bbf7d0' : '#fecaca';
                    const statusText = expired ? 'Utløpt' : maxReached ? 'Maks nådd' : c.isActive ? 'Aktiv' : 'Inaktiv';

                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700, letterSpacing: '0.06em' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 15, color: '#010f3c' }}>{c.code}</span>
                            <button
                              onClick={() => copyCode(c.code)}
                              title="Kopier kode"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: copied === c.code ? '#16a34a' : '#94a3b8' }}
                            >
                              {copied === c.code ? <FiCheckCircle size={14} /> : <FiCopy size={14} />}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b', maxWidth: 200 }}>{c.description || '—'}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#010f3c', whiteSpace: 'nowrap' }}>
                          {c.discountType === 'percent' ? `${c.discountValue}%` : `${c.discountValue} kr`}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.minOrderValue ? `${c.minOrderValue} kr` : '—'}</td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.maxUses ?? '∞'}</td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.usedCount || 0}</td>
                        <td style={{ padding: '12px 16px', color: expired ? '#dc2626' : '#64748b', whiteSpace: 'nowrap' }}>
                          {c.noExpiry ? 'Ingen' : (c.expiryDate ? c.expiryDate.split('-').reverse().join('.') : '—')}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: statusBg, color: statusColor, border: `1px solid ${statusBorder}`,
                          }}>
                            {c.isActive && !expired && !maxReached ? <FiCheckCircle size={11} /> : <FiXCircle size={11} />}
                            {statusText}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => toggleActive(c.id, c.isActive)}
                              title={c.isActive ? 'Deaktiver' : 'Aktiver'}
                              style={{
                                padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                                background: '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                color: c.isActive ? '#dc2626' : '#16a34a',
                              }}
                            >
                              {c.isActive ? 'Deaktiver' : 'Aktiver'}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(c.id)}
                              title="Slett"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {confirmModal}
    </>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modal = {
  background: '#fff', borderRadius: 12, padding: 24,
  width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};
